import * as React from "react";
import { useState, useEffect, useMemo, useRef } from "react";
import {
  SplitButton,
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuButtonProps,
  Text,
  Tooltip,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { CheckmarkCircle24Regular, Comment24Regular, Attach24Regular } from "@fluentui/react-icons";
import {
  createComment,
  updateComment,
  DiscussionCommentDto,
  CreateCommentRequest,
  UpdateCommentRequest,
} from "../../../services/discussions.service";
import { getStatusItemsBySpreadsheet, StatusItemDto } from "../../../services/status-items.service";
import { getUserFriendlyErrorMessage } from "../../../utils/error-messages";
import { useWorkflowIntelligence } from "../../../contexts/WorkflowIntelligenceContext";
import { getCommentActions, CommentAction } from "../../../utils/discussionStatusTransitions";
import RichTextInput from "./RichTextInput";
import { RichTextValue } from "./richtext/types";
import { useTrackEvent } from "../../../hooks/useTrackEvent";
import { extractUserEmails } from "./richtext/parser";
import { useTypographyStyles } from "../../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  editModeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `2px solid ${tokens.colorBrandStroke1}`,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  footerRight: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  editBanner: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
});

interface CommentComposerProps {
  discussionId: number;
  discussionType: string;
  currentStatus?: string;
  onCommentAdded: (comment: DiscussionCommentDto) => void;
  editingComment?: DiscussionCommentDto | null;
  onCommentUpdated?: (comment: DiscussionCommentDto) => void;
  onCancelEdit?: () => void;
  /**
   * Optional handler for the paperclip button in the composer footer. When
   * omitted (e.g. non-review-point discussions), the button is not rendered.
   * The parent typically wires this to a DiscussionAttachments ref that opens
   * the file picker.
   */
  onAttachClick?: () => void;
}

const MAX_COMMENT_LENGTH = 500;

/**
 * Comment input for a discussion thread. Wraps {@link RichTextInput} with
 * a 500-char limit, submit handling, and an optional "this comment changes
 * the discussion status" toggle for review-point flows.
 */
const CommentComposer: React.FC<CommentComposerProps> = ({
  discussionId,
  discussionType,
  currentStatus,
  onCommentAdded,
  editingComment,
  onCommentUpdated,
  onCancelEdit,
  onAttachClick,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const { userRole, spreadsheetId } = useWorkflowIntelligence();
  const track = useTrackEvent();
  const containerRef = useRef<HTMLDivElement>(null);

  const isEditMode = !!editingComment;

  // Get available status actions based on role, type, and current status
  const { primaryAction, menuActions } = useMemo(
    () => getCommentActions(userRole, discussionType, currentStatus),
    [userRole, discussionType, currentStatus]
  );

  // Filter out "Comment Only" from menu actions since it now has its own button
  // and get only status-changing actions for the dropdown
  const statusActions = useMemo(
    () => menuActions.filter((action) => action.status),
    [menuActions]
  );

  // Check if the user has any status-changing actions available
  const canChangeStatus = primaryAction.status !== undefined;

  const [commentValue, setCommentValue] = useState<RichTextValue>({
    markup: "",
    plainText: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [statusItems, setStatusItems] = useState<StatusItemDto[]>([]);

  // Fetch status items when component mounts
  useEffect(() => {
    if (!spreadsheetId) return;

    const fetchStatusItems = async () => {
      try {
        const items = await getStatusItemsBySpreadsheet(spreadsheetId);
        setStatusItems(items);
      } catch (error) {
        console.error("Failed to fetch status items:", error);
        setStatusItems([]);
      }
    };

    fetchStatusItems();
  }, [spreadsheetId]);

  // Populate input when entering edit mode
  useEffect(() => {
    if (editingComment) {
      setCommentValue({
        markup: editingComment.commentContent || editingComment.commentText,
        plainText: editingComment.commentText,
      });
      setSubmitError(null);
      // Scroll composer into view
      containerRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [editingComment]);

  const characterCount = commentValue.markup.length;
  const isOverLimit = characterCount > MAX_COMMENT_LENGTH;
  const isEmpty = commentValue.plainText.trim().length === 0;
  const canSubmit = !isOverLimit && !isSubmitting;

  // Comment only action (no status change)
  const commentOnlyAction: CommentAction = {
    key: "comment-only",
    label: "Comment",
    requiresComment: true,
  };

  const handleAction = async (action: CommentAction) => {
    // Validate: if action requires comment, check that comment is not empty
    if (action.requiresComment && isEmpty) {
      setSubmitError("Please enter a comment");
      return;
    }

    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Auto-generate comment text if empty and status change is selected
      const commentText = isEmpty && action.status
        ? `Status changed to ${action.status}`
        : commentValue.plainText.trim();

      const request: CreateCommentRequest = {
        commentText: commentText,
        commentContent: isEmpty ? undefined : commentValue.markup,
        taggedUserEmails: isEmpty ? undefined : extractUserEmails(commentValue.markup),
        newDiscussionStatus: action.status,
      };

      const newComment = await createComment(discussionId, request);
      track("discussion.comment_added", { discussionType });

      // Clear the input and notify parent
      setCommentValue({ markup: "", plainText: "" });
      onCommentAdded(newComment);
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditSave = async () => {
    if (isEmpty || !editingComment) return;
    if (!canSubmit) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const request: UpdateCommentRequest = {
        commentText: commentValue.plainText.trim(),
        commentContent: commentValue.markup,
        taggedUserEmails: extractUserEmails(commentValue.markup),
      };

      const updatedComment = await updateComment(
        discussionId,
        editingComment.id,
        request
      );

      setCommentValue({ markup: "", plainText: "" });
      onCommentUpdated?.(updatedComment);
    } catch (error) {
      const friendlyMessage = getUserFriendlyErrorMessage(error);
      setSubmitError(friendlyMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelEdit = () => {
    setCommentValue({ markup: "", plainText: "" });
    setSubmitError(null);
    onCancelEdit?.();
  };

  return (
    <div ref={containerRef} className={isEditMode ? styles.editModeContainer : styles.container}>
      {isEditMode && (
        <Text className={styles.editBanner}>Editing your comment...</Text>
      )}

      {submitError && (
        <div className={`${typography.errorText} ${styles.errorMessage}`}>
          <Text weight="semibold">{isEditMode ? "Error saving comment" : "Error posting comment"}</Text>
          <br />
          <Text>{submitError}</Text>
        </div>
      )}

      <RichTextInput
        value={commentValue}
        onChange={setCommentValue}
        placeholder={
          isEditMode
            ? "Edit your comment..."
            : onAttachClick
              ? "Write a comment... (paste to attach an image; @ mentions users, # tags sheets/items, [ for cell refs)"
              : "Write a comment... (type @ to mention users, # to tag sheets/items, [ for cell references)"
        }
        maxLength={MAX_COMMENT_LENGTH}
        disabled={isSubmitting}
        statusItems={statusItems}
      />

      <div className={styles.footer}>
        {/* Left-aligned attach button — only in reply mode, not while editing
            an existing comment (attachments are discussion-level, not
            per-comment, so an edit action doesn't need it). */}
        {!isEditMode && onAttachClick ? (
          <Tooltip content="Attach image" relationship="label">
            <Button
              appearance="subtle"
              icon={<Attach24Regular />}
              onClick={onAttachClick}
              disabled={isSubmitting}
              aria-label="Attach image"
            />
          </Tooltip>
        ) : <span />}

        <div className={styles.footerRight}>
        {isEditMode ? (
          <>
            <Button
              appearance="secondary"
              onClick={handleCancelEdit}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleEditSave}
              disabled={!canSubmit || isEmpty}
            >
              {isSubmitting ? "Saving..." : "Save Edit"}
            </Button>
          </>
        ) : (
          <>
            {/* Comment button - posts without status change */}
            <Tooltip content="Post comment without changing status" relationship="label">
              <Button
                appearance="secondary"
                icon={<Comment24Regular />}
                onClick={() => handleAction(commentOnlyAction)}
                disabled={!canSubmit || isEmpty}
              >
                {isSubmitting ? "Sending..." : "Comment"}
              </Button>
            </Tooltip>

            {/* Status change button - only shown if user has permission to change status */}
            {canChangeStatus && (
              statusActions.length > 0 ? (
                <Menu positioning="below-end">
                  <MenuTrigger disableButtonEnhancement>
                    {(triggerProps: MenuButtonProps) => (
                      <SplitButton
                        menuButton={triggerProps}
                        primaryActionButton={{ onClick: () => handleAction(primaryAction) }}
                        appearance="primary"
                        icon={<CheckmarkCircle24Regular />}
                        disabled={!canSubmit}
                      >
                        {primaryAction.label}
                      </SplitButton>
                    )}
                  </MenuTrigger>

                  <MenuPopover>
                    <MenuList>
                      {statusActions.map((action) => (
                        <MenuItem
                          key={action.key}
                          onClick={() => handleAction(action)}
                        >
                          {action.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
              ) : (
                <Button
                  appearance="primary"
                  icon={<CheckmarkCircle24Regular />}
                  onClick={() => handleAction(primaryAction)}
                  disabled={!canSubmit}
                >
                  {primaryAction.label}
                </Button>
              )
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default CommentComposer;
